// 认证服务API客户端
import axios, { AxiosInstance } from 'axios';

// 本地开发默认走 auth 的 nginx 代理（8080/api），避免直接访问 4433 触发跨域问题。
const KRATOS_PUBLIC_URL = process.env.REACT_APP_KRATOS_PUBLIC_URL || 'http://localhost:8080/api';

// 登录页地址：本地开发时 auth 前端通常跑在 3001 的 /auth 路径下，8080 仅作 API 代理不提供页面
const AUTH_LOGIN_URL =
  process.env.REACT_APP_AUTH_LOGIN_URL ||
  process.env.REACT_APP_AUTH_SERVICE_URL ||
  'http://localhost:3001/auth';

// 创建认证服务API实例
const authApi: AxiosInstance = axios.create({
  baseURL: KRATOS_PUBLIC_URL,
  timeout: 10000,
  withCredentials: true, // 支持cookies
});

// 会话信息接口（与 Kratos GET /sessions/whoami 返回结构一致）
// 顶层 id 为 session id，用户标识在 identity.id，用户属性在 identity.traits
export interface Session {
  id: string;
  active?: boolean;
  expires_at?: string;
  authenticated_at?: string;
  authenticator_assurance_level?: string;
  identity: {
    id: string;
    schema_id?: string;
    schema_url?: string;
    traits: {
      email?: string;
      username?: string;
      [key: string]: any;
    };
  };
}

/**
 * 将 whoami 原始返回规范为 Session（兼容有/无 identity、或 identity 在其它字段名）
 */
function normalizeWhoamiResponse(data: any): Session | null {
  if (!data || typeof data !== 'object') return null;
  // 兼容被包一层的情况：{ session: { id, identity, ... } }
  const raw = (data as Record<string, unknown>).session && typeof (data as Record<string, unknown>).session === 'object'
    ? (data as Record<string, unknown>).session as Record<string, unknown>
    : (data as Record<string, unknown>);
  // 标准 Kratos：identity 为嵌套对象
  if (raw.identity && typeof raw.identity === 'object') {
    const identity = raw.identity as Record<string, unknown>;
    if (identity.id) {
      return {
        id: String(raw.id ?? ''),
        active: raw.active as boolean | undefined,
        expires_at: raw.expires_at as string | undefined,
        authenticated_at: raw.authenticated_at as string | undefined,
        authenticator_assurance_level: raw.authenticator_assurance_level as string | undefined,
        identity: {
          id: String(identity.id),
          schema_id: identity.schema_id as string | undefined,
          schema_url: identity.schema_url as string | undefined,
          traits: (identity.traits && typeof identity.traits === 'object' ? identity.traits : {}) as Session['identity']['traits'],
        },
      };
    }
  }
  // 兼容：无 identity 但有 identity_id / traits 在顶层（部分实现或代理可能这样返回）
  const identityId = raw.identity_id ?? (raw as any).identityId;
  if (identityId) {
    return {
      id: String(raw.id ?? ''),
      active: raw.active as boolean | undefined,
      expires_at: raw.expires_at as string | undefined,
      authenticated_at: raw.authenticated_at as string | undefined,
      authenticator_assurance_level: raw.authenticator_assurance_level as string | undefined,
      identity: {
        id: String(identityId),
        traits: (raw.traits && typeof raw.traits === 'object' ? raw.traits : {}) as Session['identity']['traits'],
      },
    };
  }
  return null;
}

/**
 * 获取当前用户会话信息
 */
export const getCurrentSession = async (): Promise<Session | null> => {
  try {
    const response = await authApi.get('/sessions/whoami', {
      headers: {
        'Accept': 'application/json',
      },
    });
    const data = response.data;
    console.log('getCurrentSession response:', data);
    const session = normalizeWhoamiResponse(data);
    if (!session) {
      console.warn('getCurrentSession: whoami 返回中无 identity/identity_id，无法规范为 Session。原始 keys:', data ? Object.keys(data) : []);
    }
    return session;
  } catch (error: any) {
    console.log('getCurrentSession error:', error?.response?.data || error?.message);
    // 仅 401 视为“未登录”；网络/CORS 等异常交给上层处理，避免误判后反复跳登录页
    if (error?.response?.status === 401) {
      return null;
    }
    throw error;
  }
};

/**
 * 获取登录页面URL
 */
export const getLoginUrl = (): string => {
  let base = AUTH_LOGIN_URL.replace(/\/$/, '');
  // 兼容历史配置：若只写到 localhost:3001，则自动补齐 /auth 前缀。
  if (/^https?:\/\/localhost:3001$/i.test(base) || /^https?:\/\/127\.0\.0\.1:3001$/i.test(base)) {
    base = `${base}/auth`;
  }
  return `${base}/login`;
};
